import { ChangeEvent, ReactNode, forwardRef, useRef } from 'react'
import { Floating, Label } from '@/ui'
import { Input, InputProps, Menu, MenuItem, Plus, cn } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'

interface InputVarsProps extends InputProps {
  label: ReactNode
  items: { value: string; name: string }[]
  className?: string
}

interface VariableSelectMenuProps {
  onClose?: () => void
}

export const InputVars = forwardRef<HTMLInputElement, InputVarsProps>(function InputVars({
  items,
  label,
  value,
  onChange,
  name,
  className,
  placeholder,
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onItemSelected = (v: string) => {
    if (!inputRef?.current) {
      return
    }

    if (!value) {
      return onChange?.({ target: { value: v, name: name } } as unknown as ChangeEvent<HTMLInputElement>)
    }

    let startPos = inputRef.current.selectionStart || 0

    let oldValue = value as string
    let newValue = oldValue.substring(0, startPos) + v + oldValue.substring(startPos)

    onChange?.({ target: { value: newValue, name: name } } as unknown as ChangeEvent<HTMLInputElement>)
  }

  const VariableSelectMenu = ({ onClose = () => void 0 }: VariableSelectMenuProps) => {
    const onClickVariable = (variable: string) => {
      onClose()
      onItemSelected(variable)
    }

    return (
      <Menu>
        {items.map(it => (
          <MenuItem onClick={() => onClickVariable(it.value)} key={it.value}>
            {it.name}
          </MenuItem>
        ))}
      </Menu>
    )
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center">
        <Label $size="md" style={{ marginBottom: '0px' }}>
          {label}
        </Label>

        <Floating menu={<VariableSelectMenu />} referenceWidth={true}>
          <div className="ml-auto flex cursor-pointer items-center gap-1 text-caption-sm font-medium text-text-brand-primary hover:text-text-brand-solid">
            <Plus size={11} />
            {t('campaign.insert-var')}
          </div>
        </Floating>
      </div>

      <Input value={value} ref={inputRef} onChange={onChange} placeholder={placeholder} />
    </div>
  )
})
